import { useFormatter } from '@/api/useFormatter'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Form, Input, InputNumber, Select } from 'antd'
import { useEffect, useMemo } from 'react'
import type { DefaultOptionType } from 'antd/es/select'
import { FooterButtons } from './FooterButtons'
import { BookType, InvoiceDetail, InvoiceTransaction, TransactionDetail, TransactionRow } from '@/api/useFreight/types'
import { getSelectOptions } from '@/components/Freight/Ratebooks/Drill-in/tabs/InvoiceConfig/utils'
import { useRateManagement } from '@/api/useFreight/useFreight'
import { sortMetadataOptionsAlphabetically } from '@/components/SupplyManagement/Outages/util'
import { FormProps } from 'antd/lib'

type BaseFreightFormProps = {
  bookType: BookType
  order: TransactionRow | InvoiceTransaction | undefined
  fliDetail: InvoiceDetail | TransactionDetail | undefined
  productOptions: DefaultOptionType[]
  originOptions: DefaultOptionType[]
  destinationOptions: DefaultOptionType[]
  isTerminalsLoading?: boolean
  isStoresLoading?: boolean
  isProductOptionsLoading?: boolean
  isBaseFreightMutationPending: boolean
  isCreating: boolean
} & FormProps

export function BaseFreightForm({
  bookType,
  order,
  fliDetail,
  productOptions,
  originOptions,
  destinationOptions,
  isTerminalsLoading,
  isStoresLoading,
  isProductOptionsLoading,
  isBaseFreightMutationPending,
  isCreating,
  ...formProps
}: BaseFreightFormProps) {
  const formatter = useFormatter()
  const uom = Form.useWatch<string>('uom', formProps.form)

  const { useFilteredMetadataQuery } = useRateManagement()

  const createCounterpartyId = bookType === 'Revenue' ? order?.customer_id : order?.carrier_id
  const createCounterpartyName = bookType === 'Revenue' ? order?.customer_name : order?.carrier_name
  const { data: metadataOptions = {}, isLoading: isRatebookOptionsLoading } = useFilteredMetadataQuery({
    counterparty_id: createCounterpartyId || fliDetail?.counterparty_id,
    counterparty_name: createCounterpartyName || fliDetail?.counterparty || '',
    book_type: bookType,
  })
  const unitsOptions = useMemo(
    () =>
      getSelectOptions((metadataOptions?.ratebook_options?.rate_units || []).sort(sortMetadataOptionsAlphabetically)),
    [metadataOptions?.ratebook_options?.rate_units]
  )

  function setUOMValue(value: string | undefined) {
    formProps.form?.setFieldsValue({ uom: value })
  }

  useEffect(() => {
    const formValues = formProps.form?.getFieldsValue()
    const initialUOM = formValues?.uom
    if (!initialUOM) {
      if (fliDetail?.uom) {
        setUOMValue(fliDetail.uom)
      } else if (metadataOptions?.ratebook_options?.rate_units?.includes('gallons')) {
        setUOMValue('gallons')
      } else {
        setUOMValue((unitsOptions?.[0]?.value as string) ?? '')
      }
    }
  }, [formProps.form, metadataOptions?.ratebook_options?.rate_units])

  return (
    <Form layout='vertical' {...formProps}>
      <Vertical gap={8} className='py-2'>
        <Form.Item
          name='base_freight_subtype'
          label='Subtype'
          rules={[{ required: isCreating, message: 'Please enter a subtype' }]}
          style={{ marginBottom: 0 }}
        >
          <Input placeholder='Enter subtype' maxLength={70} />
        </Form.Item>
        <Form.Item
          name='product'
          label='Product Name'
          rules={[{ required: isCreating, message: 'Please select a product name' }]}
          style={{ marginBottom: 0 }}
        >
          <Select
            placeholder='Select product name'
            options={productOptions || []}
            showSearch
            labelInValue
            loading={isProductOptionsLoading}
            optionFilterProp={'label'}
          />
        </Form.Item>
        <Horizontal gap={8}>
          <Form.Item
            name='origin'
            label='Origin'
            rules={[{ required: isCreating, message: 'Please select an origin' }]}
            style={{ marginBottom: 0, flex: 1 }}
          >
            <Select
              placeholder='Select origin'
              options={[
                { label: 'Terminals', options: originOptions },
                { label: 'Stores', options: destinationOptions },
              ]}
              showSearch
              labelInValue
              loading={isTerminalsLoading || isStoresLoading}
              optionFilterProp={'label'}
            />
          </Form.Item>
          <Form.Item
            name='destination'
            label='Destination'
            rules={[{ required: isCreating, message: 'Please select a destination' }]}
            style={{ marginBottom: 0, flex: 1 }}
          >
            <Select
              placeholder='Select destination'
              options={[
                { label: 'Stores', options: destinationOptions },
                { label: 'Terminals', options: originOptions },
              ]}
              showSearch
              labelInValue
              loading={isStoresLoading || isTerminalsLoading}
              optionFilterProp={'label'}
            />
          </Form.Item>
        </Horizontal>
        <Form.Item
          name='bol_number'
          label='BOL Number'
          rules={[{ required: isCreating, message: 'Please enter a BOL number' }]}
          style={{ marginBottom: 0 }}
        >
          <Input placeholder='Enter BOL number' maxLength={70} />
        </Form.Item>
        <Horizontal gap={8}>
          <Form.Item name='uom' noStyle />
          <Form.Item
            name='amount'
            label='Amount'
            rules={[{ required: isCreating, message: 'Please select an amount' }]}
            style={{ marginBottom: 0, maxWidth: '50%' }}
          >
            <InputNumber
              placeholder='Enter'
              min={0}
              addonAfter={
                <Select
                  options={unitsOptions || []}
                  onChange={setUOMValue}
                  value={uom}
                  loading={isRatebookOptionsLoading}
                />
              }
            />
          </Form.Item>
          <Form.Item
            name='rate'
            label='Rate'
            rules={[{ required: isCreating, message: 'Please enter a rate' }]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber placeholder='Enter' />
          </Form.Item>
          <Form.Item dependencies={['amount', 'rate']} noStyle>
            {({ getFieldValue }) => {
              const amount = getFieldValue('amount') || 0
              const rate = getFieldValue('rate') || 0
              const total = amount * rate
              return (
                <Vertical height={'auto'} gap={4} className='px-2' alignItems='flex-end'>
                  <Texto>Total</Texto>
                  <Texto style={{ height: '100%', display: 'flex', alignItems: 'center' }} weight={'bold'}>
                    {formatter.formatCurrency(total, true, 2)}
                  </Texto>
                </Vertical>
              )
            }}
          </Form.Item>
        </Horizontal>
      </Vertical>
      <FooterButtons isLoading={isBaseFreightMutationPending} />
    </Form>
  )
}
